import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useFeatures } from "@/context/FeaturesContext";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppButton } from "@/components/WhatsApp";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { clientShipStatus } from "@/lib/clientStatus";
import { CancelAction } from "@/pages/CustomerDashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plane, Ship, MapPin, ArrowRight, CheckCircle2, Circle, Truck, Package, ShieldCheck, Star, Plus, Weight, Box, Lock, Copy, MessageSquare, FileText, AlertTriangle, Warehouse, Clock3, ChevronDown, Download } from "lucide-react";
import { curSym, shipCur } from "@/lib/currency";
import { downloadPdf } from "@/lib/download";

// MODIFIER L'EXPÉDITION (client) — uniquement AVANT réception physique au Hub.
// Réutilise PUT /shipments/{id}/details (recalcul du prix estimé via le moteur existant si impact).
function EditShipmentDialog({ s, onClose, onDone }) {
  const [dests, setDests] = useState([]);
  const [f, setF] = useState({
    dest_country: s.dest_country || "", dest_city: s.dest_city || "",
    goods_description: s.goods_description || "", tracking_number: s.tracking_number || "",
    declared_weight_kg: s.declared_weight_kg || "" });
  const [items, setItems] = useState((s.declared_items || []).map((it) => ({ ...it })));
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/countries?kind=destination").then(({ data }) => setDests(data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        dest_country: f.dest_country, dest_city: f.dest_city,
        goods_description: f.goods_description, tracking_number: f.tracking_number,
        declared_weight_kg: Number(f.declared_weight_kg) || 0 };
      if (items.length) body.items = items.map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
      const { data } = await api.put(`/shipments/${s.id}/details`, body);
      toast.success(data.price_update
        ? `Modifications enregistrées — nouveau prix estimé : ${data.price_update.new} ${curSym(data.price_update.currency)}`
        : "Modifications enregistrées");
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-shipment-dialog" className="rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifier l&rsquo;expédition — {s.reference}</DialogTitle>
          <DialogDescription>Modifiable tant que votre colis n&rsquo;est pas réceptionné au Hub. Si le prix est impacté, l&rsquo;estimation et la facture sont recalculées.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Pays de destination</Label>
            <Select value={f.dest_country} onValueChange={(v) => setF({ ...f, dest_country: v })}>
              <SelectTrigger data-testid="edit-dest-country" className="rounded-sm"><SelectValue placeholder="Pays" /></SelectTrigger>
              <SelectContent>{dests.map((c) => <SelectItem key={c.name} value={c.name}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Ville de destination</Label>
            <Input data-testid="edit-dest-city" className="rounded-sm" value={f.dest_city} onChange={(e) => setF({ ...f, dest_city: e.target.value })} /></div>
          <div className="col-span-2"><Label>Description de la marchandise</Label>
            <Textarea data-testid="edit-goods-desc" className="rounded-sm" value={f.goods_description} onChange={(e) => setF({ ...f, goods_description: e.target.value })} /></div>
          {items.map((it, i) => (
            <div key={i} className="col-span-2 flex items-center gap-2 text-sm border border-border px-3 py-2">
              <span className="flex-1 truncate">{it.custom?.name || it.name || it.article_id || `Article ${i + 1}`}</span>
              <Label className="text-xs">Quantité</Label>
              <Input data-testid={`edit-item-qty-${i}`} type="number" min="1" className="rounded-sm w-20 h-8"
                value={it.qty} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
            </div>
          ))}
          <div><Label>Poids déclaré (kg)</Label>
            <Input data-testid="edit-weight" type="number" className="rounded-sm" value={f.declared_weight_kg} onChange={(e) => setF({ ...f, declared_weight_kg: e.target.value })} /></div>
          <div><Label>N° de suivi (transporteur)</Label>
            <Input data-testid="edit-tracking" className="rounded-sm" value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Annuler</Button>
          <Button data-testid="edit-shipment-save" disabled={busy} className="rounded-sm bg-accent text-white" onClick={save}>Enregistrer les modifications</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [supplier, setSupplier] = useState({ name: "", expected_cartons: 1, goods: "" });
  const [supOpen, setSupOpen] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: "", criteria: { communication: 5, price: 5, delay: 5, reception: 5, quality: 5 } });
  const { isOn } = useFeatures();
  const { user } = useAuth();
  const [revOpen, setRevOpen] = useState(false);

  const load = () => api.get(`/shipments/${id}`).then(({ data }) => setS(data));
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [expList, setExpList] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [dispOpen, setDispOpen] = useState(false);
  const [disp, setDisp] = useState({ type: "missing", description: "" });
  const [docF, setDocF] = useState({ name: "", doc_type: "invoice", url: "" });
  const [declOpen, setDeclOpen] = useState(false);
  const [decl, setDecl] = useState({ supplier_name: "", platform: "", order_number: "", tracking_number: "", description: "", cartons_expected: 1 });
  const [invoice, setInvoice] = useState(null);
  const [instrOpen, setInstrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const loadExtras = () => {
    api.get(`/shipments/${id}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
    api.get(`/shipments/${id}/documents`).then(({ data }) => setDocuments(data)).catch(() => {});
    api.get(`/expected-packages?shipment_id=${id}`).then(({ data }) => setExpList(data)).catch(() => {});
    // 5.3B — Facture initiale du dossier (créée automatiquement à la création de l'expédition)
    if (user?.role === "customer") {
      api.get("/me/invoices").then(({ data }) =>
        setInvoice(data.find((i) => i.shipment_id === id && !i.type && i.status !== "cancelled") || null)
      ).catch(() => {});
    }
  };
  useEffect(() => { load(); loadExtras(); }, [id]);
  // Ouverture du dossier depuis le HAUT (pas de restauration d'un ancien scroll), sauf lien vers une section précise (#hash)
  useEffect(() => { if (!window.location.hash) window.scrollTo(0, 0); }, [id]);
  const copyInstructions = () => {
    const text = `INSTRUCTIONS POUR MON FOURNISSEUR — Colis2Go\n\nMerci d'expédier ma marchandise à l'adresse suivante :\n${s.receiving_address}\n\nIMPORTANT : écrire la référence ${s.reference} de façon visible sur CHAQUE carton.\nDestination finale : ${s.dest_city}, ${s.dest_country}\nMode : ${s.mode === "air" ? "Aérien" : "Maritime"}`;
    navigator.clipboard.writeText(text).then(() => toast.success(t("sd.copyInstr2")));
  };
  const sendMsg = async () => {
    if (!msgText.trim()) return;
    try { await api.post(`/shipments/${id}/messages`, { text: msgText }); setMsgText(""); loadExtras(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openDispute = async () => {
    try { await api.post(`/shipments/${id}/disputes`, disp); toast.success("Litige ouvert — le partenaire et l'équipe sont prévenus"); setDispOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const addDoc = async () => {
    if (!docF.name) { toast.error("Nom du document requis"); return; }
    await api.post(`/shipments/${id}/documents`, docF); setDocF({ name: "", doc_type: "invoice", url: "" }); toast.success("Document ajouté"); loadExtras();
  };
  const declare = async () => {
    if (!decl.supplier_name) { toast.error("Fournisseur requis"); return; }
    try {
      await api.post("/expected-packages", { ...decl, shipment_id: id, cartons_expected: Number(decl.cartons_expected) || 1 });
      toast.success("Achat déclaré — le partenaire est prévenu"); setDeclOpen(false);
      setDecl({ supplier_name: "", platform: "", order_number: "", tracking_number: "", description: "", cartons_expected: 1 });
      loadExtras();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  if (!s) return <div className="min-h-screen bg-background"><Navbar /><div className="p-10 text-muted-foreground">{t("common.loading")}</div></div>;

  const Mode = s.mode === "air" ? Plane : Ship;
  const totalWeight = (s.packages || []).reduce((a, p) => a + (p.weight_kg || 0), 0);
  const totalCbm = (s.packages || []).reduce((a, p) => a + (p.cbm || 0), 0);
  const received = (s.suppliers || []).reduce((a, x) => a + (x.received_cartons || 0), 0);
  const expected = (s.suppliers || []).reduce((a, x) => a + (x.expected_cartons || 0), 0);

  const addSupplier = async () => {
    await api.post(`/shipments/${id}/suppliers`, { ...supplier, expected_cartons: Number(supplier.expected_cartons) });
    setSupOpen(false); setSupplier({ name: "", expected_cartons: 1, goods: "" }); load();
    toast.success("Fournisseur ajouté");
  };
  const acceptFinal = async () => { try { await api.post(`/shipments/${id}/accept-final`); toast.success("Prix final validé"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const payNow = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { shipment_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de paiement"); }
  };
  const submitReview = async () => {
    try {
      const vals = Object.values(review.criteria || {});
      const rating = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : Number(review.rating);
      await api.post("/reviews", { shipment_id: id, rating, comment: review.comment, criteria: review.criteria }); toast.success("Avis publié"); setRevOpen(false);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // UX dossier client — flux prépayé « J'ai déjà ma marchandise » (estimation → facture → paiement → suivi)
  const isHubPrepaid = !!s.hub_id && !!s.prepaid_invoice;
  // Parcours « J'ai déjà ma marchandise » = dossier Hub (hors « Je veux acheter en ligne ») → page simplifiée
  const isGoods = !!s.hub_id && s.module !== "shopship";
  // « Sur devis » : aucun prix fixé → page simple, sans chronologie, sans facture ni paiement
  const isQuote = isGoods && s.final_price == null;
  const showInstructions = () => {
    setInstrOpen(true);
    setTimeout(() => document.querySelector('[data-testid="send-instructions"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };
  const estCur = curSym(invoice?.currency || shipCur(s));
  const priceAdjusted = s.real_price != null && (s.balance_due > 0 || s.refund_due > 0);
  const priceConfirmed = s.real_price != null && !priceAdjusted;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="bg-primary text-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
          <button onClick={() => navigate("/dashboard")} className="text-sm text-white/70 hover:text-accent">{t("common.back")}</button>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">{s.reference}</h1>
            <Badge data-testid="ship-client-status" className="bg-accent text-white rounded-sm">{clientShipStatus(s).label}</Badge>
          </div>
          <div className="mt-2 flex items-center gap-2 text-white/80"><Mode className="h-4 w-4 text-accent" /> {s.origin_city} <ArrowRight className="h-4 w-4" /> {s.dest_city} · {s.module === "shopship" ? `Entrepôt Colis2Go — ${s.origin_country}` : s.hub_id ? (s.partner ? `${s.hub?.name || "Hub Colis2Go"} → ${s.partner.company_name}` : (s.hub?.name || "Hub Colis2Go")) : s.partner?.company_name}</div>
          {s.departure_date && (
            <div className="mt-1.5 flex items-center gap-2 text-white/80 text-sm" data-testid="assigned-departure">
              <Clock3 className="h-4 w-4 text-accent" /> Départ prévu : <b>{s.departure_date}</b>
            </div>
          )}
          {s.module === "shopship" && <Badge className="mt-2 bg-white/15 text-white rounded-sm">Shop &amp; Ship</Badge>}
          {s.hub_id && <Badge data-testid="hub-badge" className="mt-2 bg-white/15 text-white rounded-sm gap-1"><Warehouse className="h-3.5 w-3.5" /> Hub Colis2Go</Badge>}
        </div>
      </div>

      {/* GÉRER MON EXPÉDITION — modifier / annuler tant que le colis n'est pas reçu au Hub */}
      {s.hub_id && s.status === "created" && (s.packages || []).length === 0 && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-6">
          <div className="border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="manage-shipment">
            <div className="text-sm">
              <span className="font-heading font-bold">Gérer mon expédition</span>
              <span className="text-muted-foreground"> — possible tant que votre colis n&rsquo;est pas réceptionné au Hub.</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" data-testid="edit-shipment-btn" onClick={() => setEditOpen(true)}>Modifier l&rsquo;expédition</Button>
              <CancelAction kind="shipment" id={s.id} refLabel={s.reference} onDone={load} t={t} />
            </div>
          </div>
        </div>
      )}
      {s.status === "cancelled" && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-6">
          <div className="border border-red-300 bg-red-50 p-4 text-sm" data-testid="cancelled-banner">
            <b>Expédition annulée.</b> La référence <b className="font-mono">{s.reference}</b> reste identifiable si votre colis arrive
            malgré tout au Hub — contactez-nous pour organiser un retour ou reprendre le dossier.
          </div>
        </div>
      )}
      {editOpen && <EditShipmentDialog s={s} onClose={() => setEditOpen(false)} onDone={load} />}

      {/* SUR DEVIS — page simple : pas de chronologie, pas de facture, pas de paiement tant qu'aucun prix n'existe */}
      {isQuote && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-8 space-y-4">
          <div className="border-2 border-emerald-600/40 bg-emerald-50 p-5" data-testid="quote-confirmation">
            <div className="flex items-center gap-2 font-heading font-bold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Expédition créée</div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <span>Référence : <b className="font-mono">{s.reference}</b></span>
              <Badge className={`rounded-sm ${s.quote_status === "sent" && s.quote?.amount != null ? "bg-accent text-white" : "bg-amber-100 text-amber-700"}`} data-testid="quote-status">
                {s.quote_status === "sent" && s.quote?.amount != null ? "Devis reçu — décision requise" : "En attente de devis"}
              </Badge>
            </div>
          </div>
          {s.quote_status === "sent" && s.quote?.amount != null ? (
            <QuoteDecisionPanel s={s} load={load} />
          ) : (
            <div className="border border-border bg-card p-5" data-testid="quote-info-block">
              <div className="text-xs uppercase text-muted-foreground">Sur devis</div>
              <p className="mt-1.5 text-sm">Votre marchandise doit être reçue et vérifiée au Hub Colis2Go avant que le prix puisse être déterminé.</p>
              <div className="mt-3 border border-accent/30 bg-accent/5 p-3 text-sm">
                <b>Prochaine étape :</b> Envoyez votre marchandise au Hub Colis2Go.
              </div>
              <Button data-testid="quote-see-instructions" className="mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={showInstructions}>
                Voir les instructions d&rsquo;envoi
              </Button>
            </div>
          )}
        </div>
      )}

      {/* CONFIRMATION APRÈS CRÉATION — flux prépayé : estimation → facture initiale → paiement */}
      {isHubPrepaid && !s.paid && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-8 space-y-4">
          <div className="border-2 border-emerald-600/40 bg-emerald-50 p-5" data-testid="creation-confirmation">
            <div className="flex items-center gap-2 font-heading font-bold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Expédition créée</div>
            <p className="mt-1 text-sm text-emerald-800">Votre demande d&rsquo;expédition a bien été enregistrée.</p>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <span>Référence : <b className="font-mono">{s.reference}</b></span>
              <Badge className="rounded-sm bg-amber-100 text-amber-700" data-testid="dossier-status">Expédition créée — En attente de paiement</Badge>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="border border-border bg-card p-5" data-testid="estimation-block">
              <div className="text-xs uppercase text-muted-foreground">Votre estimation</div>
              <div className="mt-1 font-heading font-extrabold text-3xl text-accent" data-testid="estimation-amount">{s.estimated_price} {estCur}</div>
              <p className="mt-2 text-xs text-muted-foreground">
                Cette estimation est basée sur les informations que vous avez déclarées.
                Le montant pourra être ajusté après réception et vérification de votre marchandise au Hub Colis2Go.
              </p>
            </div>
            <div className="border border-border bg-card p-5 space-y-3" data-testid="initial-invoice-block">
              <div className="text-xs uppercase text-muted-foreground">Facture initiale</div>
              <InvoiceCard invoice={invoice} t={t} />
              {!invoice && <p className="text-xs text-muted-foreground">Votre facture initiale est en cours de préparation.</p>}
              {isOn("payments") && invoice && (
                <Button data-testid="pay-now-btn" className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-2" onClick={payNow}>
                  <Lock className="h-4 w-4" /> {t("pay.now")} — {invoice.amount} {estCur}
                </Button>
              )}
              <p className="text-xs text-muted-foreground">Prochaine étape : paiement de votre facture initiale.</p>
            </div>
          </div>
        </div>
      )}
      {/* CONFIRMATION APRÈS PAIEMENT — résumé + prix définitif après vérification au Hub */}
      {isHubPrepaid && s.paid && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-8">
          <div className="border-2 border-emerald-600/40 bg-emerald-50 p-5" data-testid="payment-confirmation">
            <div className="flex items-center gap-2 font-heading font-bold text-emerald-700"><ShieldCheck className="h-5 w-5" /> Paiement initial reçu</div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-emerald-800">
              <span>Dossier <b className="font-mono">{s.reference}</b></span>
              <span>{s.origin_city} → {s.dest_city || s.dest_country}</span>
              {invoice && <span>Facture {invoice.number} · <b>{invoice.amount} {estCur}</b></span>}
            </div>
            {priceConfirmed && (
              <Badge className="mt-3 rounded-sm bg-emerald-600 text-white" data-testid="price-confirmed-badge">Prix définitif confirmé — Aucun ajustement nécessaire</Badge>
            )}
            {priceAdjusted && (
              <Badge className="mt-3 rounded-sm bg-amber-100 text-amber-700" data-testid="price-adjustment-badge">Ajustement de prix requis</Badge>
            )}
          </div>
        </div>
      )}

      {/* PARCOURS « J'AI DÉJÀ MA MARCHANDISE » — page simplifiée : l'essentiel d'abord,
          instructions repliables, WhatsApp en bas. Les blocs Fournisseurs / Colis attendus /
          Colis reçus / Messages / Documents / Litige / Avis restent réservés aux Achats en ligne. */}
      {isGoods && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-6" data-testid="goods-dossier-layout">
          {!isQuote && (s.items || []).length > 0 && <ItemsPanel s={s} t={t} id={id} load={load} />}
          {!isQuote && !isHubPrepaid && <PriceBlock s={s} invoice={invoice} t={t} isOn={isOn} acceptFinal={acceptFinal} payNow={payNow} />}
          {isHubPrepaid && s.paid && (
            <div className="border border-border bg-card p-5 space-y-3" data-testid="prepaid-paid-panel">
              <Badge data-testid="paid-badge" className="bg-emerald-600 text-white rounded-sm w-full justify-center py-1.5 gap-1.5"><ShieldCheck className="h-4 w-4" /> {t("pay.paid")}</Badge>
              <InvoiceCard invoice={invoice} t={t} />
              {!priceConfirmed && !priceAdjusted && (
                <p className="text-xs text-muted-foreground">Le prix définitif sera confirmé après réception et vérification de votre marchandise au Hub Colis2Go.</p>
              )}
            </div>
          )}
          {["admin", "super_admin"].includes(user?.role) && <PaymentAdminBox ship={s} onSaved={load} />}
          {/* Suivi : masqué avant paiement (flux prépayé) et pour les dossiers « Sur devis » — « Reçu au Hub Colis2Go » y apparaît */}
          {isOn("tracking") && !isQuote && (!isHubPrepaid || s.paid) && <TrackingPanel s={s} t={t} />}
          <SendInstructions s={s} t={t} isOn={isOn} copyInstructions={copyInstructions} open={instrOpen} setOpen={setInstrOpen} />
          <div className="border border-border bg-card p-5" data-testid="goods-help-block">
            <div className="font-heading font-bold text-sm">Besoin d&rsquo;aide avec cette expédition ?</div>
            <WhatsAppButton reference={s.reference} className="mt-3 w-full rounded-sm" />
          </div>
        </div>
      )}

      {!isGoods && (
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Articles déclarés (tarification par article) */}
          {(s.items || []).length > 0 && <ItemsPanel s={s} t={t} id={id} load={load} />}
          {/* Suppliers */}
          <div className="border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Package className="h-5 w-5 text-accent" /> {t("ship.suppliers")}</h2>
              <Dialog open={supOpen} onOpenChange={setSupOpen}>
                <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm gap-1" data-testid="add-supplier-btn"><Plus className="h-4 w-4" /> {t("ship.addSupplier")}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("ship.addSupplier")}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>{t("ship.supplierName")}</Label><Input data-testid="supplier-name" className="rounded-sm" value={supplier.name} onChange={(e) => setSupplier({ ...supplier, name: e.target.value })} /></div>
                    <div><Label>{t("ship.expectedCartons")}</Label><Input data-testid="supplier-cartons" type="number" className="rounded-sm" value={supplier.expected_cartons} onChange={(e) => setSupplier({ ...supplier, expected_cartons: e.target.value })} /></div>
                    <div><Label>Marchandise</Label><Input className="rounded-sm" value={supplier.goods} onChange={(e) => setSupplier({ ...supplier, goods: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button data-testid="save-supplier-btn" className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={addSupplier}>{t("common.add")}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            {expected > 0 && <div className="mt-2 text-sm font-semibold text-primary">{received} {t("ship.cartons")} reçus sur {expected}</div>}
            <div className="mt-3 space-y-2">
              {(s.suppliers || []).length === 0 && <p className="text-sm text-muted-foreground">{t("sd.addSuppliers")}</p>}
              {(s.suppliers || []).map((sup) => (
                <div key={sup.id} className="flex items-center justify-between border border-border p-3 text-sm">
                  <span className="font-medium">{sup.name} <span className="text-muted-foreground">· {sup.received_cartons}/{sup.expected_cartons} {t("ship.cartons")}</span></span>
                  <Badge className={`rounded-sm ${sup.status === "received" ? "bg-emerald-600 text-white" : sup.status === "partial" ? "bg-amber-100 text-amber-700" : "bg-secondary text-primary"}`}>
                    {sup.status === "received" ? t("ship.received") : sup.status === "partial" ? t("ship.partial") : t("ship.pending")}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Packages received */}
          <div className="border border-border bg-card p-5">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Box className="h-5 w-5 text-accent" /> {t("ship.packages")}</h2>
            {(s.packages || []).length === 0 && <p className="mt-2 text-sm text-muted-foreground">{t("sd.awaitingDepot")}</p>}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {(s.packages || []).map((p) => (
                <div key={p.id} className="border border-border p-3 text-sm">
                  <div className="font-semibold">{p.supplier_name}</div>
                  <div className="text-muted-foreground">{p.cartons} {t("ship.cartons")} · {p.weight_kg} kg · {p.cbm} CBM</div>
                  {p.photos?.length > 0 && (
                    <div className="mt-2 flex gap-2">{p.photos.slice(0, 3).map((ph, i) => <img key={i} src={ph} alt="" className="h-14 w-14 object-cover border" />)}</div>
                  )}
                </div>
              ))}
            </div>
            {(s.packages || []).length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-4 border-t pt-3 text-sm">
                <span className="flex items-center gap-1.5"><Weight className="h-4 w-4 text-accent" /> {totalWeight.toFixed(2)} kg</span>
                <span className="flex items-center gap-1.5"><Box className="h-4 w-4 text-accent" /> {totalCbm.toFixed(3)} CBM</span>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="border border-border bg-card p-5 text-center">
            {isOn("qr") && <img src={s.qr} alt="QR" className="mx-auto h-40 w-40" data-testid="shipment-qr" />}
            <div className="mt-3 text-xs uppercase text-muted-foreground">{t("ship.receivingAddress")}</div>
            <div className="mt-1 text-sm font-medium flex items-start gap-1.5 justify-center"><MapPin className="h-4 w-4 text-accent mt-0.5" /> {s.receiving_address}</div>
            <Button data-testid="copy-instructions-btn" variant="outline" size="sm" className="mt-3 rounded-sm gap-1.5 w-full" onClick={copyInstructions}><Copy className="h-4 w-4" /> Copier les instructions pour mon fournisseur</Button>
          </div>

          {/* Hub Colis2Go — décision & choix du groupageur */}
          {s.hub_id && (
            <div className="border-2 border-accent bg-accent/5 p-5" data-testid="hub-panel">
              <h3 className="font-heading font-bold flex items-center gap-2"><Warehouse className="h-4 w-4 text-accent" /> {s.hub?.name || "Hub Colis2Go"}</h3>
              {s.status === "created" && (
                <p className="mt-2 text-sm text-muted-foreground">{t("sd.hubWait")}</p>
              )}
              {["at_hub", "awaiting_partner"].includes(s.status) && (
                <>
                  <p className="mt-2 text-sm text-muted-foreground" data-testid="auto-consolidation-note">Vos colis sont préparés automatiquement pour le prochain départ correspondant à votre destination et votre mode de transport.</p>
                  <a data-testid="hub-special-request" href={`https://wa.me/447466398452?text=${encodeURIComponent(`Bonjour Colis2Go, j'ai une demande particulière concernant mon dossier ${s.reference}`)}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="mt-2 rounded-sm gap-1.5">J&rsquo;ai une demande particulière (WhatsApp)</Button>
                  </a>
                </>
              )}
              {s.status === "awaiting_partner" && (
                <p className="mt-3 text-sm text-muted-foreground" data-testid="hub-organizing-note">Colis2Go organise l&rsquo;expédition — le prix final vous sera communiqué.</p>
              )}
              {s.status === "partner_selected" && (
                <Badge data-testid="awaiting-collection-badge" className="mt-3 w-full justify-center py-1.5 rounded-sm bg-amber-100 text-amber-700">En attente de collecte par {s.partner?.company_name}</Badge>
              )}
              {s.status === "collected" && (
                <Badge data-testid="collected-badge" className="mt-3 w-full justify-center py-1.5 rounded-sm bg-emerald-600 text-white">{t("sd.collectedBy")} {s.partner?.company_name}</Badge>
              )}
            </div>
          )}

          {/* Colis attendus */}
          <div className="border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-sm">Colis attendus</h3>
              <Dialog open={declOpen} onOpenChange={setDeclOpen}>
                <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm gap-1" data-testid="declare-btn"><Plus className="h-4 w-4" /> {t("sd.declare")}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("sd.declare")}</DialogTitle><DialogDescription>{t("sd.declDesc")}</DialogDescription></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Fournisseur</Label><Input data-testid="decl-supplier" className="rounded-sm" value={decl.supplier_name} onChange={(e) => setDecl({ ...decl, supplier_name: e.target.value })} /></div>
                    <div><Label>Plateforme</Label><Input className="rounded-sm" placeholder="Alibaba…" value={decl.platform} onChange={(e) => setDecl({ ...decl, platform: e.target.value })} /></div>
                    <div><Label>N° commande</Label><Input className="rounded-sm" value={decl.order_number} onChange={(e) => setDecl({ ...decl, order_number: e.target.value })} /></div>
                    <div><Label>N° tracking</Label><Input data-testid="decl-tracking" className="rounded-sm" value={decl.tracking_number} onChange={(e) => setDecl({ ...decl, tracking_number: e.target.value })} /></div>
                    <div><Label>Cartons attendus</Label><Input data-testid="decl-cartons" type="number" className="rounded-sm" value={decl.cartons_expected} onChange={(e) => setDecl({ ...decl, cartons_expected: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Description</Label><Input className="rounded-sm" value={decl.description} onChange={(e) => setDecl({ ...decl, description: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button data-testid="decl-submit" className="rounded-sm bg-accent text-white" onClick={declare}>{t("ssd.submit")}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="mt-2 space-y-1.5">
              {expList.map((e) => (
                <div key={e.id} data-testid={`ship-expected-${e.id}`} className="flex items-center justify-between text-sm border border-border p-2">
                  <span>{e.supplier_name} · {e.cartons_expected} carton(s)</span>
                  <Badge className={`rounded-sm ${e.status === "received" ? "bg-emerald-600 text-white" : e.status === "in_transit" ? "bg-blue-100 text-blue-700" : "bg-secondary text-primary"}`}>
                    {e.status === "received" ? "Reçu" : e.status === "in_transit" ? "En route" : "Annoncé"}
                  </Badge>
                </div>
              ))}
              {expList.length === 0 && <p className="text-xs text-muted-foreground">Annoncez vos achats pour que le partenaire les attende.</p>}
            </div>
          </div>

          {/* Prices — Prix estimatif → Prix final après vérification */}
          <PriceBlock s={s} invoice={invoice} t={t} isOn={isOn} acceptFinal={acceptFinal} payNow={payNow} />

          {/* Paiement MVP — gestion manuelle par l'Admin */}
          {["admin", "super_admin"].includes(user?.role) && <PaymentAdminBox ship={s} onSaved={load} />}

          {/* Tracking */}
          {isOn("tracking") && <TrackingPanel s={s} t={t} />}

          {s.status === "delivered" && isOn("reviews") && (
            <Dialog open={revOpen} onOpenChange={setRevOpen}>
              <DialogTrigger asChild><Button data-testid="leave-review-btn" className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-2"><Star className="h-4 w-4" /> {t("ship.leaveReview")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("ship.leaveReview")}</DialogTitle><DialogDescription>{t("sd.reviewDesc")}</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  {[["communication", "Communication"], ["price", "Respect du prix"], ["delay", "Délai"], ["reception", "Réception des marchandises"], ["quality", "Qualité générale"]].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm font-medium">{label}</span>
                      <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} data-testid={`crit-${key}-${n}`} onClick={() => setReview({ ...review, criteria: { ...review.criteria, [key]: n } })}>
                          <Star className={`h-5 w-5 ${n <= (review.criteria?.[key] || 0) ? "text-amber-500 fill-amber-500" : "text-muted"}`} />
                        </button>
                      ))}</div>
                    </div>
                  ))}
                  <Textarea data-testid="review-comment" placeholder="Votre commentaire…" className="rounded-sm" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} />
                </div>
                <DialogFooter><Button data-testid="submit-review-btn" className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={submitReview}>{t("common.send")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Messagerie */}
          {isOn("messaging") && (
          <div className="border border-border bg-card p-5">
            <h3 className="font-heading font-bold text-sm flex items-center gap-2"><MessageSquare className="h-4 w-4 text-accent" /> Messages</h3>
            <div className="mt-3 max-h-56 overflow-y-auto space-y-2" data-testid="messages-list">
              {messages.map((m) => (
                <div key={m.id} className={`text-sm p-2 border ${m.sender_role === "customer" ? "bg-secondary/60 border-border ml-6" : "bg-card border-accent/30 mr-6"}`}>
                  <div className="text-[11px] text-muted-foreground">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</div>
                  {m.text}
                </div>
              ))}
              {messages.length === 0 && <p className="text-xs text-muted-foreground">{t("sd.noMessages")}</p>}
            </div>
            <div className="mt-3 flex gap-2">
              <Input data-testid="message-input" className="rounded-sm" placeholder="Écrire un message…" value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} />
              <Button data-testid="message-send" className="rounded-sm bg-primary text-white shrink-0" onClick={sendMsg}>{t("common.send")}</Button>
            </div>
          </div>
          )}

          {/* Documents */}
          <div className="border border-border bg-card p-5">
            <h3 className="font-heading font-bold text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-accent" /> Documents</h3>
            <div className="mt-2 space-y-1.5" data-testid="documents-list">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm border border-border p-2">
                  <span>{d.name} {d.reference && <span className="text-muted-foreground">· {d.reference}</span>}</span>
                  <Badge className="rounded-sm bg-secondary text-primary">{d.doc_type?.toUpperCase()}</Badge>
                </div>
              ))}
              {documents.length === 0 && <p className="text-xs text-muted-foreground">Aucun document pour le moment.</p>}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Input data-testid="doc-name" className="rounded-sm" placeholder="Nom (ex: Facture)" value={docF.name} onChange={(e) => setDocF({ ...docF, name: e.target.value })} />
              <Input className="rounded-sm" placeholder="Lien (optionnel)" value={docF.url} onChange={(e) => setDocF({ ...docF, url: e.target.value })} />
            </div>
            <Button data-testid="doc-add" size="sm" variant="outline" className="mt-2 rounded-sm gap-1 w-full" onClick={addDoc}><Plus className="h-4 w-4" /> Ajouter un document</Button>
          </div>

          {/* WhatsApp / assistance — placé après les informations essentielles du dossier */}
          <WhatsAppButton reference={s.reference} className="w-full rounded-sm" />

          {/* Litige */}
          {isOn("disputes") && (
          <Dialog open={dispOpen} onOpenChange={setDispOpen}>
            <DialogTrigger asChild><Button data-testid="open-dispute-btn" variant="outline" className="w-full rounded-sm gap-2 text-red-600 border-red-200 hover:bg-red-50"><AlertTriangle className="h-4 w-4" /> {t("sd.report")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("sd.report")}</DialogTitle><DialogDescription>{t("sd.disputeDesc")}</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("sd.issueType")}</Label>
                  <Select value={disp.type} onValueChange={(v) => setDisp({ ...disp, type: v })}>
                    <SelectTrigger data-testid="dispute-type" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[["missing", "Colis manquant"], ["weight", "Poids contesté"], ["cbm", "CBM contesté"], ["damaged", "Colis endommagé"], ["price", "Prix différent"], ["delay", "Retard"], ["delivery", "Problème de livraison"], ["other", "Autre"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Description</Label><Textarea data-testid="dispute-desc" className="rounded-sm" value={disp.description} onChange={(e) => setDisp({ ...disp, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="dispute-submit" className="rounded-sm bg-accent text-white" onClick={openDispute}>Ouvrir le litige</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>
      )}
    </div>
  );
}



// Chronologie CLIENT simplifiée pour le parcours Hub (« J'ai déjà ma marchandise »).
// Les étapes internes (Décision client, Groupageur sélectionné, Collecté) restent côté Admin.
const hubClientTimeline = (s) => {
  const ms = Object.fromEntries((s.milestones || []).map((m) => [m.key, m]));
  const later = ["shipped", "in_transit", "arrived", "available", "delivered"].includes(s.status);
  const arrivedPlus = ["arrived", "available", "delivered"].includes(s.status);
  return [
    { key: "created", label: "Expédition créée", done: true, at: ms.created?.at },
    { key: "paid", label: "Paiement initial reçu", done: !!s.paid, at: s.paid_at },
    { key: "received", label: "Reçu au Hub Colis2Go", done: !!ms.received?.done || (s.packages || []).length > 0 || later, at: ms.received?.at },
    { key: "verified", label: "Marchandise vérifiée", done: s.real_price != null || later, at: null },
    { key: "price_confirmed", label: "Prix définitif confirmé", done: (s.real_price != null && !s.balance_due && !s.refund_due) || later, at: null },
    { key: "ready", label: "Prêt au départ", done: !!ms.collected?.done || later, at: ms.collected?.at },
    { key: "shipped", label: "Expédié", done: !!ms.shipped?.done || later, at: ms.shipped?.at },
    { key: "arrived", label: "Arrivé à destination", done: !!ms.arrived?.done || arrivedPlus, at: ms.arrived?.at },
    { key: "delivered", label: "Disponible / Remis au destinataire", done: !!ms.delivered?.done || ["available", "delivered"].includes(s.status), at: ms.delivered?.at },
  ];
};

// DÉCISION CLIENT SUR DEVIS — Accepter (→ facture + Payer maintenant) ou Annuler l'expédition.
// Confirmation obligatoire avant chaque action ; une seule transition possible (verrou backend).
function QuoteDecisionPanel({ s, load }) {
  const [confirm, setConfirm] = useState(null); // "accept" | "cancel"
  const [busy, setBusy] = useState(false);
  const q = s.quote || {};
  const cur = curSym(q.currency || shipCur(s));
  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shipments/${s.id}/quote-decision`, { decision });
      toast.success(decision === "accept"
        ? `Devis accepté — facture ${data.invoice_number} à régler (${data.amount} ${cur})`
        : "Expédition annulée — aucun paiement n'est dû");
      setConfirm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setConfirm(null); }
    setBusy(false);
  };
  const rows = [
    ["Référence", s.reference],
    ["Contenu déclaré", s.goods_description || "—"],
    ["Poids déclaré", s.declared_weight_kg ? `${s.declared_weight_kg} kg` : "—"],
    ["Mode de transport", s.mode === "air" ? "Aérien" : s.mode === "sea" ? "Maritime" : s.mode || "—"],
    ["Destination", `${s.dest_city || ""} ${s.dest_country || ""}`.trim() || "—"],
  ];
  return (
    <div className="border-2 border-accent/50 bg-card p-5 space-y-3" data-testid="quote-decision-panel">
      <div className="flex items-center gap-2 font-heading font-bold"><FileText className="h-5 w-5 text-accent" /> Votre devis Colis2Go</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm border border-border bg-secondary/40 p-3">
        {rows.map(([l, v]) => (
          <React.Fragment key={l}>
            <span className="text-muted-foreground">{l}</span>
            <span className="font-semibold">{v}</span>
          </React.Fragment>
        ))}
        {(s.declared_items || []).length > 0 && (
          <>
            <span className="text-muted-foreground">Articles</span>
            <span>{s.declared_items.map((it) => `${it.qty || 1} × ${it.name}`).join(", ")}</span>
          </>
        )}
      </div>
      <div className="border border-border p-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">Montant du devis</div>
          <div data-testid="client-quote-amount" className="font-heading font-extrabold text-3xl text-accent">{q.amount} {cur}</div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          Envoyé le {q.sent_at ? new Date(q.sent_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
        </div>
      </div>
      {q.note && <p data-testid="client-quote-note" className="text-sm border border-border bg-secondary/60 p-2.5 whitespace-pre-wrap">{q.note}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button data-testid="client-quote-accept" disabled={busy} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          onClick={() => setConfirm("accept")}><CheckCircle2 className="h-4 w-4" /> Accepter le devis</Button>
        <Button data-testid="client-quote-cancel" disabled={busy} variant="outline" className="rounded-sm text-destructive border-destructive/40"
          onClick={() => setConfirm("cancel")}>Annuler l&rsquo;expédition</Button>
      </div>
      <p className="text-xs text-muted-foreground">Une question sur ce devis ? Contactez Colis2Go sur WhatsApp.</p>
      {confirm && (
        <Dialog open onOpenChange={(v) => !v && setConfirm(null)}>
          <DialogContent data-testid="quote-decision-confirm" className="rounded-sm">
            <DialogHeader>
              <DialogTitle>{confirm === "accept" ? "Accepter le devis ?" : "Annuler l'expédition ?"}</DialogTitle>
              <DialogDescription>
                {confirm === "accept"
                  ? `Vous acceptez le devis de ${q.amount} ${cur} pour ${s.reference}. La facture sera activée et le paiement requis. L'acceptation ne vaut pas paiement.`
                  : `Cette annulation est définitive : le dossier ${s.reference} sera annulé et aucune facture ne sera due.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" data-testid="quote-decision-no" disabled={busy} className="rounded-sm" onClick={() => setConfirm(null)}>Retour</Button>
              <Button data-testid="quote-decision-yes" disabled={busy}
                className={`rounded-sm text-white ${confirm === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
                onClick={() => decide(confirm)}>
                {confirm === "accept" ? "Oui, j'accepte le devis" : "Oui, annuler définitivement"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Facture initiale — bloc réutilisé (en-tête du dossier et panneau latéral)
function InvoiceCard({ invoice, t }) {
  if (!invoice) return null;
  return (
    <div className="flex items-center justify-between border border-border bg-secondary/50 px-3 py-2" data-testid="shipment-invoice">
      <div>
        <div className="font-mono font-semibold text-sm">{invoice.number}</div>
        <div className="text-xs text-muted-foreground">{t("inv.initial")}</div>
      </div>
      <div className="text-right space-y-1">
        <div className="font-bold text-sm" data-testid="shipment-invoice-amount">{invoice.amount} {curSym(invoice.currency)}</div>
        <Badge className={`rounded-sm ${invoice.status === "paid" ? "bg-emerald-600 text-white" : invoice.status === "refunded" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`} data-testid="shipment-invoice-status">
          {invoice.status === "paid" ? `${t("inv.paid")} ✓` : invoice.status === "refunded" ? t("inv.refunded") : t("inv.toPay")}
        </Badge>
        {invoice.status === "paid" && (
          <Button size="sm" variant="outline" data-testid="download-receipt-btn" className="rounded-sm h-7 gap-1 text-xs w-full"
            onClick={() => downloadPdf(`/invoices/${invoice.id}/receipt`, `recu-${invoice.number}.pdf`)}>
            <Download className="h-3.5 w-3.5" /> Reçu PDF
          </Button>
        )}
      </div>
    </div>
  );
}

// Résumé des articles déclarés (réutilisé : parcours marchandise + achats en ligne)
function ItemsPanel({ s, t, id, load }) {
  return (
    <div className="border border-border bg-card p-5" data-testid="items-panel">
      <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Package className="h-5 w-5 text-accent" /> Vos articles</h2>
      <div className="mt-3 space-y-1.5 text-sm">
        {s.items.map((l, i) => (
          <div key={i} data-testid={`item-line-${i}`} className="flex items-center justify-between border-b border-dashed border-border pb-1">
            <span>{l.qty} × {l.name}</span>
            <span className="font-semibold">
              {l.status === "priced" ? `${l.unit_price} × ${l.qty} = ${l.line_total} ${l.currency || s.items_currency || ""}` :
                l.status === "approval" ? <Badge className="rounded-sm bg-orange-100 text-orange-700">Approbation requise</Badge> :
                <Badge className="rounded-sm bg-amber-100 text-amber-700">{t("common.onQuote")}</Badge>}
            </span>
          </div>
        ))}
        {s.weight_line && (
          <div className="flex items-center justify-between border-b border-dashed border-border pb-1">
            <span>{s.weight_line.name}</span>
            <span className="font-semibold">{s.weight_line.line_total != null ? `${s.weight_line.line_total} ${s.items_currency || ""}` : t("common.onQuote")}</span>
          </div>
        )}
        {s.items_fees > 0 && <div className="flex items-center justify-between text-muted-foreground"><span>Frais destination</span><span>{s.items_fees} {s.items_currency}</span></div>}
        <div className="flex items-center justify-between pt-1">
          <span className="font-heading font-bold">{t("sd.totalEst")}</span>
          <span data-testid="items-total" className="font-heading font-extrabold text-xl text-accent">{s.items_total} {s.items_currency}</span>
        </div>
        <p className="text-[11px] text-muted-foreground">{t("sd.indicative")}</p>
      </div>
      {(s.price_adjustments || []).map((a, i) => (
        <div key={i} data-testid={`price-adjustment-${i}`} className="mt-3 border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold text-amber-800">{t("sd.adjusted")}</div>
          <div className="mt-1 text-amber-800">{a.reason}</div>
          <div className="mt-1">Ancien : <s>{a.old_total} {s.items_currency}</s> → Nouveau : <b>{a.new_total} {s.items_currency}</b></div>
          {!a.confirmed && (
            <Button size="sm" data-testid="confirm-adjustment-btn" className="mt-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={async () => { await api.post(`/shipments/${id}/confirm-adjustment`); toast.success("Ajustement confirmé"); load(); }}>
              Confirmer le nouveau prix
            </Button>
          )}
          {a.confirmed && <Badge className="mt-2 rounded-sm bg-emerald-600 text-white">{t("sd.confirmed")}</Badge>}
        </div>
      ))}
    </div>
  );
}

// Prix estimatif → Prix final après vérification + facture initiale (hors flux prépayé)
function PriceBlock({ s, invoice, t, isOn, acceptFinal, payNow }) {
  const cur = curSym(invoice?.currency || shipCur(s));
  return (
    <div className="border border-border bg-card p-5 space-y-3">
      {s.final_price != null ? (
        <>
          {s.estimated_price != null && (
            <div className="text-xs text-muted-foreground" data-testid="initial-estimate">
              {t("ship.initialEstimate")} <span className="line-through">{s.estimated_price} {cur}</span> {t("ship.afterVerification")}
            </div>
          )}
          <div className="text-xs uppercase text-muted-foreground">{s.quote_status === "accepted" ? "Prix du devis accepté" : t("ship.finalPrice")}</div>
          <div className="font-heading font-extrabold text-3xl text-accent" data-testid="final-price">{s.final_price} {cur}</div>
          {s.quote_status === "accepted" && (
            <p className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="quote-accepted-note">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              Devis accepté{s.quote?.accepted_at ? ` le ${new Date(s.quote.accepted_at).toLocaleDateString("fr-FR")}` : ""} — montant verrouillé
            </p>
          )}
          {!s.final_accepted && <Button data-testid="accept-final-btn" className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={acceptFinal}>{t("ship.acceptFinal")}</Button>}
          {s.final_accepted && !s.paid && isOn("payments") && (
            <>
              <Button data-testid="pay-now-btn" className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-2" onClick={payNow}>
                <Lock className="h-4 w-4" /> {t("pay.now")} — {s.final_price} {cur}
              </Button>
              <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {t("pay.protectedNote")}</p>
            </>
          )}
          {s.final_accepted && !s.paid && !isOn("payments") && (
            <div className="space-y-2" data-testid="manual-payment-block">
              <Badge className="rounded-sm bg-amber-100 text-amber-700 w-full justify-center py-1.5" data-testid="payment-pending-badge">
                {s.payment_status === "cancelled" ? t("pay.cancelledBadge") : s.payment_status === "refunded" ? t("pay.refundedBadge") : t("pay.pendingBadge")}
              </Badge>
              {s.payment_link && (
                <a href={s.payment_link} target="_blank" rel="noopener noreferrer">
                  <Button data-testid="payment-link-btn" className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-2"><ShieldCheck className="h-4 w-4" /> {t("pay.cardBtn")}</Button>
                </a>
              )}
              {s.payment_note && (
                <p data-testid="payment-note" className="text-xs border border-border bg-secondary/60 p-2.5 whitespace-pre-wrap">{s.payment_note}</p>
              )}
              {!s.payment_link && !s.payment_note && (
                <p className="text-xs text-muted-foreground">{t("pay.manualNote")}</p>
              )}
            </div>
          )}
          {s.paid && <Badge data-testid="paid-badge" className="bg-emerald-600 text-white rounded-sm w-full justify-center py-1.5 gap-1.5"><ShieldCheck className="h-4 w-4" /> {t("pay.paid")}</Badge>}
        </>
      ) : s.estimated_price != null ? (
        <>
          <div className="text-xs uppercase text-muted-foreground">{t("ship.estimatedPrice")}</div>
          <div className="font-heading font-extrabold text-3xl text-accent" data-testid="estimated-price">{s.estimated_price} {cur}</div>
          <p className="text-xs text-muted-foreground">{t("ship.estimatedNote")}</p>
        </>
      ) : (
        <div className="text-sm text-muted-foreground" data-testid="price-pending">{t("ship.onQuote")}</div>
      )}
      {/* 5.3B — Facture initiale liée au dossier (générée automatiquement) */}
      <InvoiceCard invoice={invoice} t={t} />
    </div>
  );
}

// Suivi de l'expédition (chronologie client simplifiée pour les dossiers Hub)
function TrackingPanel({ s, t }) {
  return (
    <div className="border border-border bg-card p-5">
      <h3 className="font-heading font-bold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> {t("ship.tracking")}</h3>
      <ol className="mt-4 space-y-4">
        {(s.hub_id ? hubClientTimeline(s) : (s.milestones || [])).map((m, i, arr) => (
          <li key={m.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              {m.done ? <CheckCircle2 className="h-5 w-5 text-accent" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
              {i < arr.length - 1 && <span className={`w-px flex-1 mt-1 ${m.done ? "bg-accent" : "bg-border"}`} style={{ minHeight: 18 }} />}
            </div>
            <div className="pb-1">
              <div className={`text-sm font-semibold ${m.done ? "text-primary" : "text-muted-foreground"}`}>{m.label}</div>
              {m.at && <div className="text-xs text-muted-foreground">{new Date(m.at).toLocaleString()}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// Instructions d'envoi — section repliable (Hub, adresse, QR, copie) placée après estimation/facture/paiement
function SendInstructions({ s, t, isOn, copyInstructions, open: openProp, setOpen: setOpenProp }) {
  const [selfOpen, setSelfOpen] = useState(false);
  const open = openProp !== undefined ? openProp : selfOpen;
  const toggle = () => (setOpenProp ? setOpenProp(!open) : setSelfOpen(!open));
  return (
    <div className="border border-border bg-card" data-testid="send-instructions">
      <button type="button" data-testid="send-instructions-toggle" className="w-full flex items-center justify-between p-5 text-left" onClick={toggle}>
        <span className="font-heading font-bold text-sm flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-accent" /> Instructions d&rsquo;envoi — {s.hub_name || s.hub?.name || "Hub Colis2Go"}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-center">
          {isOn("qr") && <img src={s.qr} alt="QR" className="mx-auto h-40 w-40" data-testid="shipment-qr" />}
          <div className="mt-3 text-xs uppercase text-muted-foreground">{t("ship.receivingAddress")}</div>
          <div className="mt-1 text-sm font-medium flex items-start gap-1.5 justify-center"><MapPin className="h-4 w-4 text-accent mt-0.5" /> {s.receiving_address}</div>
          <Button data-testid="copy-instructions-btn" variant="outline" size="sm" className="mt-3 rounded-sm gap-1.5 w-full" onClick={copyInstructions}><Copy className="h-4 w-4" /> Copier les instructions</Button>
        </div>
      )}
    </div>
  );
}

const PAY_METHODS = [
  ["cash", "Espèces"],
  ["orange_money", "Orange Money"],
  ["wave", "Wave"],
  ["bank_transfer", "Virement bancaire"],
  ["card_stripe", "Carte via Stripe"],
  ["other", "Autre"],
];

function PaymentAdminBox({ ship, onSaved }) {
  const [f, setF] = useState({
    method: ship.payment_method === "card_link" ? "card_stripe" : (ship.payment_method || "cash"),
    link: ship.payment_link || "",
    note: ship.payment_note || "",
  });
  const [busy, setBusy] = useState(false);
  const isStripe = f.method === "card_stripe";
  const alreadyPaid = !!ship.paid;
  // status = "confirmed" enregistre RÉELLEMENT le paiement (facture payée + transaction en base).
  // status = "" sauvegarde seulement lien/instructions sans rien confirmer.
  const submit = async (status) => {
    if (isStripe && !f.link.trim()) { toast.error("Lien Stripe requis pour la méthode « Carte via Stripe »"); return; }
    if (f.link && !/^https:\/\//.test(f.link.trim())) { toast.error("Le lien de paiement doit commencer par https://"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/shipments/${ship.id}/payment`,
        { status, method: f.method, link: isStripe ? f.link : "", note: f.note });
      if (status === "confirmed") {
        // Succès affiché UNIQUEMENT si la transaction est bien enregistrée en base (retour backend)
        if (!data.invoice) throw new Error("Transaction non enregistrée — paiement NON confirmé");
        toast.success(`Paiement accompli — facture ${data.invoice.invoice_number} payée (${data.invoice.amount} ${curSym(data.invoice.currency)})`);
      } else if (status === "refunded") {
        toast.success("Paiement marqué remboursé");
      } else if (status === "cancelled") {
        toast.success("Paiement annulé");
      } else {
        toast.success("Lien / instructions enregistrés (paiement non confirmé)");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    setBusy(false);
  };
  return (
    <div className="border border-border bg-card p-5" data-testid="payment-admin-box">
      <h3 className="font-heading font-bold text-sm">Paiement (Admin — enregistrement manuel)</h3>
      {alreadyPaid ? (
        <div className="mt-3 space-y-2.5">
          <Badge data-testid="pay-already-paid" className="rounded-sm bg-emerald-600 text-white w-full justify-center py-1.5 gap-1.5">
            <ShieldCheck className="h-4 w-4" /> Paiement accompli — {ship.paid_amount} {curSym(shipCur(ship))}
          </Badge>
          <p className="text-xs text-muted-foreground">
            {PAY_METHODS.find(([v]) => v === (ship.payment_method === "card_link" ? "card_stripe" : ship.payment_method))?.[1] || ""}
            {ship.paid_at ? ` · ${new Date(ship.paid_at).toLocaleString("fr-FR")}` : ""}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="pay-refund" variant="outline" size="sm" disabled={busy} className="rounded-sm" onClick={() => submit("refunded")}>Marquer remboursé</Button>
            <Button data-testid="pay-cancel" variant="outline" size="sm" disabled={busy} className="rounded-sm text-destructive" onClick={() => submit("cancelled")}>Annuler le paiement</Button>
          </div>
          <Button data-testid="pay-receipt" variant="outline" size="sm" className="w-full rounded-sm gap-1.5"
            onClick={() => downloadPdf(`/admin/shipments/${ship.id}/payment-receipt`, `recu-${ship.reference}.pdf`)}>
            <Download className="h-3.5 w-3.5" /> Reçu de paiement (PDF)
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          <div>
            <Label className="text-xs">Méthode de paiement</Label>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger data-testid="pay-method" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isStripe && (
            <div>
              <Label className="text-xs">Lien Stripe Payment Link <span className="text-red-500">*</span></Label>
              <Input data-testid="pay-link" className="rounded-sm h-9" placeholder="https://buy.stripe.com/..." value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} />
            </div>
          )}
          <div>
            <Label className="text-xs">Instructions (ex : Orange Money, Wave, virement)</Label>
            <Textarea data-testid="pay-note" className="rounded-sm" rows={3} placeholder={"Orange Money GN : +224 XXX XX XX XX\nNom : Colis2Go\nRéférence : " + ship.reference} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
          <Button data-testid="pay-save" disabled={busy} className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" onClick={() => submit("confirmed")}>
            <ShieldCheck className="h-4 w-4" /> Confirmer le paiement reçu
          </Button>
          <Button data-testid="pay-save-draft" disabled={busy} variant="outline" className="w-full rounded-sm" onClick={() => submit("")}>
            Enregistrer lien / instructions (sans confirmer)
          </Button>
        </div>
      )}
    </div>
  );
}
